import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowLeft, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useContent } from '../context/ContentContext';
import { bundlesData } from '../data/content';

const priceNum = (p: { price: string }) => parseInt(p.price.replace(/\D/g, ''));

export const CartDrawer: React.FC = () => {
  const { cart, cartGroups, removeFromCart, removeBundle, updateQuantity, cartTotal, addToCart, addBundle, isCartOpen, closeCart } = useCart();

  const { products } = useContent();
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);

  // Lock body scroll while drawer is open
  React.useEffect(() => {
    if (isCartOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isCartOpen]);

  const suggestion = (() => {
    const ids = new Set(cart.map((c) => c.id));
    const has = (id: string) => ids.has(id);
    const SHAMPOO = 'salt-free-shampoo';
    const SCALP_SHAMPOO = 'scalp-balance-shampoo';
    const OIL = 'hair-growth-oil';
    const MASK = 'hair-repair-mask';
    const NIGHT = 'night-renewal-cream';
    const ANTI = 'anti-aging-cream';
    const FOOT = 'foot-care-oil';
    const hairIds = [SHAMPOO, SCALP_SHAMPOO, OIL, MASK];
    const faceIds = [NIGHT, ANTI];
    const hairCount = hairIds.filter((id) => has(id)).length;
    const faceCount = faceIds.filter((id) => has(id)).length;
    const anyShampoo = has(SHAMPOO) || has(SCALP_SHAMPOO);

    // Bundle upgrades first
    if (faceCount >= 2 && !has('skin-glow-set')) {
      const bundle = bundlesData.find((b) => b.id === 'skin-glow-set');
      if (bundle) return { type: 'bundle' as const, bundle };
    }
    if (hairCount >= 2 && !has('hair-repair-set')) {
      const bundle = bundlesData.find((b) => b.id === 'hair-repair-set');
      if (bundle) return { type: 'bundle' as const, bundle };
    }

    // Hair line
    if (anyShampoo && has(OIL) && !has(MASK)) {
      const p = products.find((x) => x.id === MASK);
      if (p) return { type: 'product' as const, product: p };
    }
    if (anyShampoo && !has(OIL)) {
      const p = products.find((x) => x.id === OIL);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(OIL) && !anyShampoo) {
      const p = products.find((x) => x.id === SHAMPOO);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(MASK) && !anyShampoo) {
      const p = products.find((x) => x.id === SHAMPOO);
      if (p) return { type: 'product' as const, product: p };
    }

    // Face line
    if (has(NIGHT) && !has(ANTI)) {
      const p = products.find((x) => x.id === ANTI);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(ANTI) && !has(NIGHT)) {
      const p = products.find((x) => x.id === NIGHT);
      if (p) return { type: 'product' as const, product: p };
    }

    // Foot care — complementary nourishing oil
    if (has(FOOT) && !has(OIL)) {
      const p = products.find((x) => x.id === OIL);
      if (p) return { type: 'product' as const, product: p };
    }

    return null;
  })();


  const handleAddSuggested = () => {
    if (!suggestion) return;
    if (suggestion.type === 'product') {
      const p = suggestion.product;
      addToCart({ id: p.id, name: p.name, price: priceNum(p), quantity: 1, image: p.image });
    } else {
      const b = suggestion.bundle;
      const items = b.productIds
        .map((id) => products.find((p) => p.id === id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p));
      const original = items.reduce((s, p) => s + priceNum(p), 0);
      const total =
        (b as any).priceOverride != null
          ? Math.round((b as any).priceOverride)
          : Math.round(original * (1 - b.discount));
      addBundle({
        slug: b.id,
        title: b.name,
        image: b.image,
        price: total,
        discountCode: (b as any).discountCode,
        components: items.map((p) => ({
          id: p.id,
          name: p.name,
          price: priceNum(p),
          image: p.image,
          quantity: 1,
        })),
      });
    }
  };


  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={closeCart}
            className="fixed inset-0 bg-[#412018]/60 backdrop-blur-sm z-[60]"
          />

          {/* Drawer panel — slides from the right edge of the screen */}
          <motion.aside
            dir="rtl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: [0.19, 1, 0.22, 1], duration: 0.6 }}
            className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[460px] md:w-[500px] bg-[#EAE6D7] text-[#412018] z-[70] shadow-2xl flex flex-col"
          >
            <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply"></div>

            {/* Header — close on visual-left, title+icon on visual-right (RTL) */}
            <div className="relative flex items-center justify-between px-6 py-5 border-b border-[#412018]/10 bg-[#412018] text-[#EAE6D7]">
              <div className="flex items-center gap-3">
                <ShoppingBag size={22} className="text-[#fbbf25]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#fbbf25]">ההזמנה שלך</div>
                  <h2 className="font-serif text-2xl md:text-3xl leading-tight">סל הקניות</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={closeCart}
                aria-label="סגור סל קניות"
                className="text-[#EAE6D7]/80 hover:text-[#fbbf25] transition-colors hover:rotate-90 duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fbbf25] focus-visible:ring-offset-2 focus-visible:ring-offset-[#412018] rounded"
              >
                <X size={22} aria-hidden="true" />
              </button>
            </div>


            {/* Body */}
            <div className="relative flex-1 overflow-y-auto px-6 py-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="bg-white p-6 rounded-full mb-6 text-[#412018]/20 shadow-sm">
                    <ShoppingBag size={40} />
                  </div>
                  <h3 className="text-xl font-serif mb-3">סל הקניות שלך ריק</h3>
                  <p className="text-[#412018]/60 font-light mb-8 px-4">
                    נראה שעדיין לא הוספת מוצרים לסל. את מוזמנת לבקר בבוטיק שלנו.
                  </p>
                  <Link
                    to="/shop"
                    onClick={closeCart}
                    className="bg-[#412018] text-[#fbbf25] px-7 py-3 uppercase tracking-widest font-bold text-xs hover:bg-[#67392b] transition-colors flex items-center gap-3"
                  >
                    <span>למעבר לבוטיק</span>
                    <ArrowLeft size={14} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-5">
                  {cartGroups.map((group) => {
                    if (group.type === 'bundle') {
                      return (
                        <div
                          key={`bundle-${group.bundleId}`}
                          className="pb-5 border-b border-[#412018]/10"
                        >
                          <div className="flex gap-4">
                            <div className="w-20 h-20 bg-white overflow-hidden border border-[#412018]/5 flex-shrink-0 relative">
                              {group.bundleImage ? (
                                <img src={group.bundleImage} alt={group.bundleTitle} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full grid grid-cols-2 gap-px bg-[#412018]/20">
                                  {group.items.slice(0, 4).map((it) => (
                                    <img key={it.id} src={it.image} alt="" className="w-full h-full object-cover" />
                                  ))}
                                </div>
                              )}
                              <div className="absolute top-0 right-0 bg-[#fbbf25] text-[#412018] p-0.5">
                                <Package size={10} />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-[9px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-1">מארז</div>
                                  <h4 className="font-serif text-xl md:text-2xl font-medium leading-snug text-[#412018] line-clamp-2">
                                    {group.bundleTitle}
                                  </h4>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeBundle(group.bundleId)}
                                  aria-label={`הסר את ${group.bundleTitle} מהסל`}
                                  className="text-[#412018]/40 hover:text-red-500 transition-colors flex-shrink-0"
                                >
                                  <Trash2 size={16} aria-hidden="true" />
                                </button>
                              </div>
                              <div className="mt-auto pt-3 flex items-center justify-between">
                                <span className="text-xs text-[#412018]/60">{group.items.length} פריטים</span>
                                <div className="font-bold text-[#67392b] tabular-nums">₪{group.bundlePrice}</div>
                              </div>
                            </div>
                          </div>
                          <ul className="mt-3 pl-24 space-y-1">
                            {group.items.map((it) => (
                              <li key={it.lineId ?? it.id} className="text-xs text-[#412018]/70 flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-[#412018]/40 flex-shrink-0" />
                                <span className="truncate">{it.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    const item = group.item;
                    return (
                      <div key={item.id} className="flex gap-4 pb-5 border-b border-[#412018]/10">
                        <Link to={`/shop/${item.id}`} onClick={closeCart} className="w-20 h-20 bg-white overflow-hidden border border-[#412018]/5 flex-shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </Link>
                        <div className="flex-1 min-w-0 flex flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <Link to={`/shop/${item.id}`} onClick={closeCart} className="font-serif text-2xl md:text-3xl font-medium leading-snug hover:text-[#67392b] transition-colors line-clamp-2">
                              {item.name}
                            </Link>

                            <button
                              type="button"
                              onClick={() => removeFromCart(item.id)}
                              aria-label={`הסר את ${item.name} מהסל`}
                              className="text-[#412018]/40 hover:text-red-500 transition-colors flex-shrink-0"
                            >
                              <Trash2 size={16} aria-hidden="true" />
                            </button>
                          </div>
                          <div className="mt-auto pt-3 flex items-center justify-between">
                            <div className="flex items-center border border-[#412018]/20 rounded-full px-2 py-0.5 bg-white">
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="p-1 hover:text-[#67392b] transition-colors"
                                aria-label={`הפחת כמות עבור ${item.name}`}
                              >
                                <Minus size={12} aria-hidden="true" />
                              </button>
                              <span className="w-7 text-center text-sm tabular-nums" aria-live="polite" aria-label={`כמות: ${item.quantity}`}>{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="p-1 hover:text-[#67392b] transition-colors"
                                aria-label={`הוסף כמות עבור ${item.name}`}
                              >
                                <Plus size={12} aria-hidden="true" />
                              </button>
                            </div>
                            <div className="font-bold text-[#67392b] tabular-nums">₪{item.price * item.quantity}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}


                  {suggestion && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="bg-white border border-[#412018]/10 p-4 flex items-center gap-3"
                    >
                      <div className="w-14 h-14 bg-[#F9F7F2] overflow-hidden flex-shrink-0">
                        <img
                          src={suggestion.type === 'product' ? suggestion.product.image : suggestion.bundle.image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-1">
                          <span>
                            {suggestion.type === 'product'
                              ? 'מומלץ עבורך'
                              : `שדרגי למארז — חסכו ${Math.round(suggestion.bundle.discount * 100)}%`}
                          </span>
                        </div>
                        <p className="font-serif text-2xl md:text-3xl font-medium leading-snug text-[#412018] truncate">
                          {suggestion.type === 'product' ? suggestion.product.name : suggestion.bundle.name}
                        </p>
                      </div>
                      <button
                        onClick={handleAddSuggested}
                        className="bg-[#412018] text-[#fbbf25] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#67392b] transition-colors flex-shrink-0"
                      >
                        הוסיפי
                      </button>
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="relative border-t border-[#412018]/10 bg-white/60 backdrop-blur-sm px-6 py-5 space-y-4">
                <div className="flex justify-between text-sm text-[#412018]/70">
                  <span>סכום ביניים</span>
                  <span className="tabular-nums">₪{cartTotal}</span>
                </div>
                <div className="flex justify-between text-xs text-[#412018]/50">
                  <span>משלוח</span>
                  <span>חישוב בקופה</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-[#67392b] border-t border-[#412018]/10 pt-3">
                  <span>סה״כ</span>
                  <span className="tabular-nums">₪{cartTotal}</span>
                </div>
                <label className="flex items-start gap-2 text-xs text-[#412018]/80 select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#412018] flex-shrink-0 cursor-pointer"
                  />
                  <span>
                    אני מאשר/ת שקראתי ואני מסכים/ה ל
                    <Link
                      to="/policies/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#67392b] underline hover:text-[#412018] transition-colors mx-1"
                    >
                      תקנון האתר
                    </Link>
                  </span>
                </label>
                <Link
                  to="/cart"
                  onClick={(e) => {
                    if (!agreedToTerms) {
                      e.preventDefault();
                      return;
                    }
                    closeCart();
                  }}
                  aria-disabled={!agreedToTerms}
                  className={`w-full py-4 uppercase tracking-[0.2em] transition-colors duration-500 flex items-center justify-center gap-4 group font-bold text-sm ${
                    agreedToTerms
                      ? 'bg-[#412018] text-[#fbbf25] hover:bg-[#67392b] hover:text-white'
                      : 'bg-[#412018]/40 text-[#fbbf25]/70 cursor-not-allowed pointer-events-none'
                  }`}
                >
                  <span>מעבר לתשלום</span>
                  <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
